import DefaultButton from "../../../../component/base/DefaultButton"; //* Importa o componente DefaultButton
import { useNavigate } from 'react-router-dom'; //* Importa o hook useNavigate
import { useEffect, useState } from 'react';

import { RiArrowGoBackLine } from "react-icons/ri"; //* Importa o icone de voltar
import { RiEdit2Line } from "react-icons/ri"; //* Importa o icone de edição

import moment from "moment"; //* Importa o moment para formatação de datas

//? Importing types
import { CompletedCorrection } from "../../../../hooks/corrections/types/types";

import { getApplicationStatusText } from "../../../../utils/applications/status";
import { getStatusBadgeClass } from "../../../../utils/applications/status";
import { useApplications } from "../../../../hooks/applications/useApplications"; 

const corporateColors = [
  { hex: '#1e3a8a', name: 'Azul Índigo' },       // Azul escuro/índigo
  { hex: '#0066cc', name: 'Azul Corporativo' },  // Azul corporativo padrão
  { hex: '#003366', name: 'Azul Marinho' },      // Azul marinho
  { hex: '#4682B4', name: 'Azul Aço' },          // Azul aço
  { hex: '#2c3e50', name: 'Azul Ardósia' },      // Azul ardósia escuro
  { hex: '#34495e', name: 'Azul Cinza' },        // Azul acinzentado
  { hex: '#006400', name: 'Verde Escuro' },      // Verde escuro
  { hex: '#2e8b57', name: 'Verde Mar' },         // Verde mar
  { hex: '#006b54', name: 'Verde Esmeralda' },   // Verde esmeralda
  { hex: '#800000', name: 'Bordô' },             // Bordô
  { hex: '#8b0000', name: 'Vermelho Escuro' },   // Vermelho escuro
  { hex: '#9b2226', name: 'Vermelho Rubi' },     // Vermelho rubi
  { hex: '#4b0082', name: 'Índigo' },            // Índigo
  { hex: '#663399', name: 'Roxo Rebeca' },       // Roxo rebeca
  { hex: '#654321', name: 'Marrom' },            // Marrom
  { hex: '#8B4513', name: 'Marrom Sela' },       // Marrom sela
  { hex: '#008080', name: 'Cerceta' },           // Verde-azulado/cerceta
  { hex: '#20b2aa', name: 'Verde-Mar Claro' },   // Verde-mar claro
];

interface CorrectionHeaderProps {
  completedCorrection: CompletedCorrection;
  aplicationId?: string;
  diagramId?: string;
}

/**
 * @Component CorrectionHeader
 * @Description Displays the header for the correction view, including correction details,
 * contest information, and application details.
 * @param {CorrectionHeaderProps} props - The props for the CorrectionHeader component.
 * @returns {JSX.Element} The CorrectionHeader component.
 */
export function CorrectionHeader({
  completedCorrection,
  aplicationId,
  diagramId,
}: CorrectionHeaderProps) {
  const navigate = useNavigate(); // Inicializando o hook useNavigate
  const [headerColor, setHeaderColor] = useState(corporateColors[0]);
  const token = localStorage.getItem("token") || "";
  const { getApplicationById } = useApplications(token);
  const [applicationDetails, setApplicationDetails] = useState<any>(null);
  

  const correctionType = completedCorrection?.correctionType
    ? "dissertative"
    : "objective";


  // Função para selecionar uma cor aleatória da lista de cores corporativas
  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * corporateColors.length);
    setHeaderColor(corporateColors[randomIndex]);
  }, []);
  
  // Busca informações adicionais da aplicação no modo dissertativo
  useEffect(() => {
    const fetchApplicationDetails = async () => {
      if (correctionType === "dissertative" && aplicationId) {
        try {
          const details = await getApplicationById(aplicationId);
          if (details) {
            setApplicationDetails(details);
          }
        } catch (error) {
          console.error("Erro ao buscar detalhes da aplicação:", error);
        }
      }
    };

    fetchApplicationDetails();
  }, [aplicationId, correctionType, getApplicationById]);
  
  const handleGoBack = () => {
    // Obter o ID da aplicação da fonte correta dependendo do tipo de correção
    const applicationId = correctionType === 'dissertative' && applicationDetails 
      ? applicationDetails.id 
      : (completedCorrection.application?.id || aplicationId);
    
    // Se temos um ID de aplicação, redireciona para a página de visualização de testes
    if (applicationId) {
      navigate(`/tests/view/${applicationId}?tab=3`);
    } else {
      // Fallback para o comportamento anterior caso não tenhamos ID da aplicação
      navigate(-1);
    }
  };

  // Título do card baseado no tipo de correção
  const cardTitle = correctionType === 'objective' 
    ? "Informaçoes da Folha de Resposta e Aplicação" 
    : "Informações da Aplicação";

  return (
    <>
      {/* Header with reduced padding and size */}
      <div className="card border-0 shadow-sm mb-3">
        <div 
          className="card-header py-2" 
          style={{ backgroundColor: headerColor.hex, color: 'white' }}
          title={`Cor: ${headerColor.name}`}
        >
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="h4 mb-0">
              {completedCorrection.description || "Detalhes da Correção"}
            </h2>
            <div className="d-flex gap-2">
              <DefaultButton
                bg="warning"
                text="Editar"
                icon={<RiEdit2Line />}
                to={`/corrections/edit/${aplicationId}/${diagramId}/${completedCorrection.id}`}
              />
              <DefaultButton
                bg="secondary"
                text="Voltar"
                icon={<RiArrowGoBackLine />}
                onClick={handleGoBack}
              />
            </div>
          </div>
        </div>
        <div className="card-body bg-white py-2">
          <div className="row g-2">
            <div className="col-md-3 d-flex align-items-center">
              <div className="d-flex align-items-center">
                <span className="fw-bold me-2">Status:</span>
                <span
                  className={`badge ${getStatusBadgeClass(
                    completedCorrection.status || ""
                  )} text-white px-2 py-1`}
                >
                  {completedCorrection.status
                    ? getApplicationStatusText(completedCorrection.status)
                    : "N/A"}
                </span>
              </div>
            </div>
            <div className="col-md-4 d-flex align-items-center">
              <span className="fw-bold me-2">Data da Correção:</span>
              <span>
                {completedCorrection.correctionDate
                  ? moment(completedCorrection.correctionDate).format(
                      "DD/MM/YYYY HH:mm"
                    )
                  : "N/A"}
              </span>
            </div>
            <div className="col-md-5 d-flex align-items-center justify-content-between">
              <div>
                <span className="fw-bold me-2">Arquivo:</span>
                <span>{completedCorrection.fileLabel || "N/A"}</span>
              </div>
              {completedCorrection.filePath && (
                <a
                  className="btn btn-outline-primary btn-sm"
                  href={completedCorrection.filePath}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="bi bi-download me-1"></i>
                  Baixar Arquivo
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Unified Card for Contest and Application Details - with reduced padding and spacing */}
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-header bg-white border-bottom py-2">
          <h3 className="h6 mb-0 text-primary">
            <i className="bi bi-clipboard-check me-2"></i>
            {cardTitle}
          </h3>
        </div>
        <div className="card-body py-2">
          <div className="row g-3">
            {/* Contest Details Column - Só exibir no modo objetivo */}
            {correctionType === 'objective' && (
              <div className="col-lg-6">
                <div className="card bg-light border-0">
                  <div className="card-body py-2 px-3">
                    <h6 className="card-subtitle mb-2 text-muted">
                      <i className="bi bi-journal-text me-2"></i>
                      Detalhes da Folha de Resposta
                    </h6>

                    {completedCorrection.answerSheet && (
                      <ul className="list-group list-group-flush mb-0">
                        <li className="list-group-item border-0 bg-light ps-0 py-1">
                          <span className="fw-bold">Título:</span>{" "}
                          <span className="text-dark">
                            {completedCorrection.answerSheet?.contestTitle}
                          </span>
                        </li>
                        <li className="list-group-item border-0 bg-light ps-0 py-1">
                          <span className="fw-bold">Subtítulo:</span>{" "}
                          <span className="text-dark">
                            {completedCorrection.answerSheet?.contestSubtitle}
                          </span>
                        </li>
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Application Details Column - Reorganized to have horizontal layout */}
            <div className={correctionType === 'objective' ? "col-lg-6" : "col-lg-12"}>
              {(completedCorrection.application || applicationDetails) && (
                <div className="card bg-light border-0">
                  <div className="card-body py-2 px-3">
                    <h6 className="card-subtitle mb-2 text-muted">
                      <i className="bi bi-clipboard-check me-2"></i>
                      Detalhes da Aplicação
                    </h6>

                    <div className="row">
                      {/* Basic Info Column */}
                      <div className="col-md-6">
                        <div className="mb-1">
                          <span className="fw-bold">Nome:</span>{" "}
                          <span className="text-dark">
                            {correctionType === 'dissertative' && applicationDetails 
                              ? applicationDetails.name 
                              : completedCorrection.application?.name}
                          </span>
                        </div>
                        <div className="mb-1">
                          <span className="fw-bold">Tipo:</span>{" "}
                          <span className="badge bg-secondary">
                            {correctionType === 'dissertative' && applicationDetails
                              ? (applicationDetails.isOnline ? "Online" : "Presencial")
                              : (completedCorrection.application?.isOnline ? "Online" : "Presencial")}
                          </span>
                        </div>
                        <div className="mb-1">
                          <span className="fw-bold">Status:</span>{" "}
                          <span
                            className={`badge ${getStatusBadgeClass(
                              correctionType === 'dissertative' && applicationDetails
                                ? applicationDetails.status
                                : completedCorrection.application?.status || ""
                            )}`}
                          >
                            {getApplicationStatusText(
                              correctionType === 'dissertative' && applicationDetails
                                ? applicationDetails.status
                                : completedCorrection.application?.status || ""
                            )}
                          </span>
                        </div>
                      </div>

                      {/* Dates Column */}
                      <div className="col-md-6">
                        <div className="mb-1">
                          <span className="fw-bold">Início:</span>{" "}
                          <span className="text-dark">
                            {correctionType === 'dissertative' && applicationDetails
                              ? (applicationDetails.startDate
                                ? moment(applicationDetails.startDate).format("DD/MM/YYYY HH:mm")
                                : "N/A")
                              : (completedCorrection.application?.startDate
                                ? moment(completedCorrection.application.startDate).format("DD/MM/YYYY HH:mm")
                                : "N/A")}
                          </span>
                        </div>
                        <div className="mb-1">
                          <span className="fw-bold">Término:</span>{" "}
                          <span className="text-dark">
                            {correctionType === 'dissertative' && applicationDetails
                              ? (applicationDetails.endDate
                                ? moment(applicationDetails.endDate).format("DD/MM/YYYY HH:mm")
                                : "N/A")
                              : (completedCorrection.application?.endDate
                                ? moment(completedCorrection.application.endDate).format("DD/MM/YYYY HH:mm")
                                : "N/A")}
                          </span>
                        </div>
                        <div>
                          <span className="fw-bold">Data Limite:</span>{" "}
                          <span className="text-dark">
                            {correctionType === 'dissertative' && applicationDetails
                              ? (applicationDetails.deadlineDate
                                ? moment(applicationDetails.deadlineDate).format("DD/MM/YYYY HH:mm")
                                : "N/A")
                              : (completedCorrection.application?.deadlineDate
                                ? moment(completedCorrection.application.deadlineDate).format("DD/MM/YYYY HH:mm")
                                : "N/A")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}